<template>
    <v-card class="mt-4 w-full ">

        <div class="d-flex justify-space-between pr-4">

            <!-- New field button -->
            <div class="text-h6 pa-8">{{ data.title.charAt(0).toUpperCase() + (data.title).slice(1) }}</div>
            <v-btn class="mt-8 mr-8" color="indigo-darken-4" @click="isAddingField = true">add field</v-btn>

            <!-- New field dialog -->
            <v-dialog v-model="isAddingField" max-width="600px" v-if="isAddingField">
                <v-card>
                    <v-card-title>
                        Add field
                    </v-card-title>
                    <v-card-text>
                        <v-form @submit.prevent="submitAddField">
                            <!-- Field title -->
                            <v-text-field v-model="newFieldModel.name" label="Field title" required></v-text-field>
                            <!-- Field type -->
                            <v-select clearable v-model="newFieldModel.type"
                                :items="['Text', 'Number', 'Boolean', 'Date']" label="Field type" />
                            <div v-if="newFieldModel.type === 'Text'">
                                <v-text-field v-model="newFieldModel.text" label="Field value" required></v-text-field>
                            </div>
                            <div v-if="newFieldModel.type === 'Number'">
                                <v-number-input v-model="newFieldModel.text" label="Field value"
                                    required></v-number-input>
                            </div>
                            <div v-if="newFieldModel.type === 'Boolean'">
                                <v-switch v-model="newFieldModel.text" label="Field value"
                                    color="indigo darken-4"></v-switch>
                            </div>
                            <div v-if="newFieldModel.type === 'Date'">
                                <v-date-input v-model="newFieldModel.text" label="Field value"
                                    prepend-icon=""></v-date-input>
                            </div>
                            <!-- Switch to indicate if a field is editable -->
                            <v-switch label="Editable field" v-model="newFieldModel.editable"
                                color="indigo darken-4"></v-switch>
                        </v-form>
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <!-- Cancel button -->
                        <v-btn color="indigo darken-4" text @click="isAddingField = false">Cancel</v-btn>
                        <!-- Add button-->
                        <v-btn color="indigo darken-4" text @click="() => submitAddField(data.title)">Add field</v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

        </div>

        <v-spacer></v-spacer>

        <!-- Loop to display all fields -->
        <div class="d-flex justify-space-between flex-wrap">
            <div v-for="(field, index) in fields" :key="index" class="d-flex flex-column w-50 mb-4 px-8">

                <!-- Field title -->
                <div class="text-subtitle-1 font-weight-bold d-flex">
                    {{ field.name }}
                    <!-- Edit button -->
                    <div v-if="editableFieldsClient[field.name] || editableFields[field.name]"><v-btn
                            :icon="editButtonImage(field.name)"
                            style="font-size:8px;color:black;margin-right:-5px;margin-top:-2px" variant="text"
                            density="compact" @click="toggleEditing(field.name)" />
                    </div>
                </div>

                <!-- Field edition mode -->
                <div v-if="(props.editableFields[field.name] || editableFieldsClient[field.name]) && (props.isEditing || isEditingField === field.name)">
                    <!-- Boolean -->
                    <div v-if="typeof field.text === 'boolean'">
                        <v-switch v-model="field.text"></v-switch>
                    </div>
                    <!-- Text -->
                    <div v-else-if="typeof field.text === 'string'">
                        <v-text-field v-model="field.text"></v-text-field>
                    </div>
                    <!-- Number -->
                    <div v-else-if="typeof field.text === 'number'">
                        <v-text-field v-model="field.text"></v-text-field>
                    </div>
                    <!-- Date -->
                    <div v-else-if="field.text.seconds">
                    <v-text-field v-model="field.text"></v-text-field>
                    </div>
                </div>

                <div v-else>{{ formatTimestamp(field.text) }}</div>

            </div>
        </div>
    </v-card>
</template>

<script setup>
import { defineProps, ref, watch } from 'vue';
import * as rosepetalModel from "rosepetal-model";

let $meta              = rosepetalModel.default.meta;

const props            = defineProps({

    project:        Object,
    data:           Object,
    isEditing:      Boolean,
    editableFields: Object,
})

// Array to store the fields of the current data
const fields            = ref(props.data.fields.map(field => ({ ...field })));

fields.value.forEach(field => {
    if (typeof field.text === 'object') {
        field.text = new Date(field.text.seconds * 1000).toLocaleString();
    }
});

const originalFields    = ref(JSON.parse(JSON.stringify(fields.value)));
const isAddingField     = ref(false);
const newFieldModel     = ref({
    name: '',
    text: null,
    type: null,
    editable: false,
})

// Variable to store the field name of the field currently being edited (single edit button)
const isEditingField        = ref('');

// Array to store the new editable fields added by the user
const editableFieldsClient  = ref({});

function editButtonImage(fieldName) {
    for (let key in props.editableFields) {
        if (key === fieldName && props.editableFields[key]) {
            return isEditingField.value === fieldName ? 'fa-solid fa-floppy-disk' : 'fa-solid fa-pen-to-square';
        }
    }

    for (let key in editableFieldsClient.value) {
        if (key === fieldName && editableFieldsClient.value[key]) {
            return isEditingField.value === fieldName ? 'fa-solid fa-floppy-disk' : 'fa-solid fa-pen-to-square';
        }
    }
}

// Toggle editing mode
function toggleEditing(fieldName) {
    isEditingField.value === fieldName ? isEditingField.value = '' : isEditingField.value = fieldName;
}


function resetNewFieldModel() {
    newFieldModel.value.name        = '';
    newFieldModel.value.text        = '';
    newFieldModel.value.type        = null;
    newFieldModel.value.editable    = false;
}

function resetValue() {
    newFieldModel.value.text = null;
}

function submitEditedFields() {
    
    const arrayofEditableFields = [
        ...Object.keys(props.editableFields).filter((key) => props.editableFields[key] === true),
        ...Object.keys(editableFieldsClient.value).filter((key) => editableFieldsClient.value[key] === true),
    ];
    const fieldsToEdit = []
    
    // If the field name is in the array of editable fields and the field value is different from the original value,
    // save it to the firestore and update the original field value
    fields.value.forEach((field) => {
        if (arrayofEditableFields.includes(field.name)) {
            const originalField = originalFields.value.find((originalField) => originalField.name === field.name);
            if (originalField && originalField.text !== field.text) {
                fieldsToEdit.push({
                    name: field.name,
                    text: field.text,
                    location: props.data.title,
                });
                originalFields.value.forEach((originalField) => {
                    if (originalField.name === field.name) {
                        originalField.text = field.text;    
                    }
                });
            }
        }
    });
    if (fieldsToEdit.length > 0) {
        $meta.setEditedFields(fieldsToEdit, props.project.id);
    }
}

// Add a new field and reset the new field model
function submitAddField(location) {
    isAddingField.value        = false;
    const newField             = {

        name:       newFieldModel.value.name,
        text:       newFieldModel.value.text,
        type:       newFieldModel.value.type,
        editable:   newFieldModel.value.editable,
        location:   location,
    };

    if (newField.type === 'Date') {
        newField.text = new Date(newField.text)
    }
    $meta.setNewFields(newField, props.project.id).then(() => {
        if (newField.type === 'Date') {
            newField.text = newField.text.toLocaleString();
        }
        fields.value.push(newField);
        originalFields.value.push({ name: newField.name, text: newField.text });
        editableFieldsClient.value[newField.name] = newField.editable;
        resetNewFieldModel();
    });
}

// Formatting function for date values
function formatTimestamp(value) {
    if (typeof value === "object" && value.seconds) {
        return value.toDate().toLocaleString();
    }
    return value;
}

// Save edited fields (global edit button)
watch(() => props.isEditing, (newValue, oldValue) => {
    if (!newValue && oldValue) {
        submitEditedFields();   
    }
});

// Save edited fields (single edit button)
watch(() => isEditingField.value, (newValue, oldValue) => {
    if (!newValue && oldValue) {
        submitEditedFields();
    }
});

// Reset field values
watch(() => isAddingField.value, (newValue) => {
    if (!newValue) {
        resetNewFieldModel();
    }
});

// Default value for boolean type
watch(() => newFieldModel.value.type, (newValue) => {
    if (newValue === 'Boolean') {
        newFieldModel.value.text = false;
        return;
    }
    if (newValue) {
        resetValue();
    }
});
</script>
